/**
 * Ringtone utility — generates a classic phone ringtone using the Web Audio API.
 *
 * Pattern: two short rings (~0.4s each) separated by a short pause (~0.2s),
 * then a longer pause (~2s) before repeating.  Total cycle ≈ 3s.
 *
 * Usage:
 *   const ringer = createRingtone();
 *   ringer.start();   // begins looping ringtone
 *   ringer.stop();    // stops immediately
 */

let audioCtx = null;

function getAudioContext() {
  if (!audioCtx || audioCtx.state === 'closed') {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  // Resume suspended context (browser autoplay policy)
  if (audioCtx.state === 'suspended') {
    audioCtx.resume().catch(() => {});
  }
  return audioCtx;
}

/**
 * Play a dual-tone burst (classic phone ring: 440 Hz + 480 Hz)
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 * @param {number} startTime — when to start (ctx.currentTime-based)
 * @param {number} duration — burst length in seconds
 */
function scheduleBurst(ctx, masterGain, startTime, duration) {
  // Two oscillators for the dual-tone ring
  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  const burstGain = ctx.createGain();

  osc1.type = 'sine';
  osc1.frequency.value = 440; // Hz

  osc2.type = 'sine';
  osc2.frequency.value = 480; // Hz

  // Envelope — quick fade in/out to avoid clicks
  burstGain.gain.setValueAtTime(0, startTime);
  burstGain.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
  burstGain.gain.setValueAtTime(0.3, startTime + duration - 0.02);
  burstGain.gain.linearRampToValueAtTime(0, startTime + duration);

  osc1.connect(burstGain);
  osc2.connect(burstGain);
  burstGain.connect(masterGain);

  osc1.start(startTime);
  osc1.stop(startTime + duration);
  osc2.start(startTime);
  osc2.stop(startTime + duration);
}

/**
 * Schedule one full ring cycle (two bursts + pauses)
 * @param {AudioContext} ctx
 * @param {GainNode} masterGain
 * @param {number} cycleStart
 * @returns {number} — end time of the cycle
 */
function scheduleRingCycle(ctx, masterGain, cycleStart) {
  // Burst 1: 0.4s ring
  scheduleBurst(ctx, masterGain, cycleStart, 0.4);
  // Pause: 0.2s
  // Burst 2: 0.4s ring
  scheduleBurst(ctx, masterGain, cycleStart + 0.6, 0.4);
  // Long pause: 2.0s
  // Total cycle: 0.4 + 0.2 + 0.4 + 2.0 = 3.0s
  return cycleStart + 3.0;
}

export function createRingtone() {
  let running = false;
  let masterGain = null;
  let intervalId = null;
  let ctx = null;

  function start() {
    if (running) return;
    running = true;

    try {
      ctx = getAudioContext();
      masterGain = ctx.createGain();
      masterGain.gain.value = 0.6; // overall volume
      masterGain.connect(ctx.destination);

      // Schedule the first cycle immediately
      let nextCycleTime = ctx.currentTime + 0.05;
      scheduleRingCycle(ctx, masterGain, nextCycleTime);
      nextCycleTime += 3.0;

      // Keep scheduling cycles every 3 seconds
      intervalId = setInterval(() => {
        if (!running || !ctx) return;
        // Schedule a bit ahead to avoid gaps
        const now = ctx.currentTime;
        if (nextCycleTime < now + 0.1) {
          nextCycleTime = now + 0.1;
        }
        scheduleRingCycle(ctx, masterGain, nextCycleTime);
        nextCycleTime += 3.0;
      }, 2800); // slightly less than 3s to ensure overlap-free scheduling
    } catch (e) {
      console.warn('Ringtone: Web Audio API not available', e);
      running = false;
    }
  }

  function stop() {
    running = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (masterGain) {
      try {
        // Quick fade out to avoid click
        const now = ctx?.currentTime || 0;
        masterGain.gain.cancelScheduledValues(now);
        masterGain.gain.setValueAtTime(masterGain.gain.value, now);
        masterGain.gain.linearRampToValueAtTime(0, now + 0.05);
        // Disconnect after fade
        setTimeout(() => {
          try { masterGain.disconnect(); } catch (_) {}
        }, 100);
      } catch (_) {}
      masterGain = null;
    }
  }

  return { start, stop };
}
